import { Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { fadeAnimation } from 'src/animations';
import { CustomerOverviewModel, PaginationResponse } from 'src/app/classes';
import { CustomerPipe } from 'src/app/lib/customer/pipes/customer.pipe';
import { Customer } from '../../../../customer/classes';
import { CustomerService } from '../../../../customer/services/customer.service';

@Component({
  selector: 'app-select-customer',
  templateUrl: './select-customer.component.html',
  styleUrls: ['./select-customer.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectCustomerComponent),
      multi: true
    },
    CustomerPipe
  ],
  animations: [fadeAnimation]
})
export class SelectCustomerComponent implements ControlValueAccessor, OnDestroy, OnInit {

  currentCustomer: Customer;

  currentLabel: string = "Kunde";
  @Input() set label(value: string) {
    if (value) this.currentLabel = value;
    else this.currentLabel = "Kunde";
  }

  @Input() placeholder: string = "Kunde suchen";
  @Input() appearance: string = 'standard';
  @Input() hideDisabledCustomerHint: boolean = false;

  @Output() focus: EventEmitter<void> = new EventEmitter();
  @Output() selectionChanged: EventEmitter<number> = new EventEmitter();

  @ViewChild("inputField", { static: true }) inputField: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  public customers: CustomerOverviewModel[] = [];

  formGroup: FormGroup;

  private _loading = new BehaviorSubject(false);
  private _subscriptions: Subscription[] = [];

  /**
   * the constructor creates a new instance of the select component
   */
  constructor(
    private _formBuilder: FormBuilder,
    private _customerService: CustomerService,
    private _customerPipe: CustomerPipe
  ) {
    this.formGroup = this._formBuilder.group({
      'value': null,
      'filter': null
    });
  }
  /**
   * the method returns the display string
   * @param customer 
   * @returns 
   */
  display(customer: Customer): Observable<string> {
    return this._customerPipe.transform(customer.customerid);
  }

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
  }
  /**
   * the method is called on component initialization
   */
  ngOnInit(): void {
    this._subscriptions.push(...[
      this._customerService.customers$.pipe(take(1), takeUntil(this._loading.pipe(filter(x => x)))).subscribe(customers => {
        this.customers = (customers as any[]);
      }),
      this.filterControl
        .valueChanges
        .pipe(
          debounceTime(1000),
          distinctUntilChanged()
        )
        .subscribe(
          (value) => this.search(value)
        )
    ]);
  }
  /**
   * the method emits the focus event
   */
  onFocus() {
    this.focus.emit();
  }

  public onTouched: () => void = () => { };

  registerOnChange(fn: any): void {
    this
      ._subscriptions
      .push(
        this
          .valueControl
          .valueChanges
          .subscribe(fn)
      );
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  search(term: string) {
    if (typeof term != 'string') term = '';
    let normalized = term.trim().toLocaleLowerCase();
    this._customerService
      .searchCustomersByString(normalized, { 'pageNumber': 1, 'pageSize': 20 })
      .subscribe({ next: (response: PaginationResponse<CustomerOverviewModel[]>) => this.customers = response.content });
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.formGroup.disable() : this.formGroup.enable();
  }

  selectCustomer(event: MatAutocompleteSelectedEvent) {
    if (!event || !event.option || !event.option.value) {
      this.currentCustomer = null;
      this.label = "Kunde";
      this.filterControl.setValue('');
      this.selectionChanged.emit(null);
      if (this.valueControl.value) this.valueControl.setValue(null);
    } else if (this.currentCustomer !== event.option.value) {
      this.currentCustomer = event.option.value;
      if (this.currentCustomer) this.display(this.currentCustomer).subscribe((value) => this.label = value);
      this.filterControl.setValue('');
      this.selectionChanged.emit(event.option.value ? (event.option.value as Customer).customerid : null);
      if (this.valueControl.value !== event.option.value.customerid) this.valueControl.setValue(event.option.value ? event.option.value.customerid : null);
    }
    this.inputField.nativeElement.blur();
    if (this.autocomplete && this.autocomplete.panelOpen) this.autocomplete.closePanel();
  }

  setCustomerIfOneLeft() {
    if (this.customers.length === 1) this.selectCustomer({ option: { value: this.customers[0] } } as MatAutocompleteSelectedEvent);
  }

  writeValue(val: any): void {
    this.valueControl.patchValue(val);
    if (typeof val === 'number') this._customerService.getCustomerById(val, true).pipe(take(1)).subscribe((customer: Customer) => this.selectCustomer({ option: { value: customer } } as any));
  }

  // controls
  get valueControl(): AbstractControl {
    return this.formGroup.controls['value'];
  }
  get filterControl(): AbstractControl {
    return this.formGroup.controls['filter'];
  }

}